import { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Dialog, Transition } from '@headlessui/react'
import Switch from 'react-switch'
import { useNavigate } from 'react-router'
import { useI18n } from '../provider/i18n'
import { AuthContext } from '../provider/auth'
import {
  useAcceptGlobalConsentsMutation,
  useGetClientQuery,
} from '../generated/urql.client'
import { Green600 } from '../lib/colors'
import { RegulationLinks } from '../lib/regulations'

type MissingGlobalConsentsForm = {
  regulationsAccepted: boolean
  medicalDocumentationAccepted: boolean
}

export const MissingGlobalConsentsModal = () => {
  const i18n = useI18n()
  const navigate = useNavigate()
  const { clientId, isLogged } = useContext(AuthContext)
  const [error, setError] = useState<string>()

  const [{ data }, reexecuteGetClient] = useGetClientQuery({
    variables: { id: clientId },
    pause: !isLogged || clientId < 0,
  })

  const [{ fetching }, acceptGlobalConsents] =
    useAcceptGlobalConsentsMutation()

  const { handleSubmit, control, reset } = useForm<MissingGlobalConsentsForm>({
    defaultValues: {
      regulationsAccepted: false,
      medicalDocumentationAccepted: false,
    },
  })

  const client = data?.client_by_pk
  const isMissing =
    !!client &&
    (!client.regulationsAcceptedAt || !client.medicalDocumentationAcceptedAt)

  useEffect(() => {
    if (isMissing) {
      reset()
      setError(undefined)
    }
  }, [isMissing, reset])

  const doSubmit = useCallback(
    async (form: MissingGlobalConsentsForm) => {
      setError(undefined)
      const { error: mutationError } = await acceptGlobalConsents(form)

      if (mutationError) {
        setError(mutationError.message)
        return
      }

      reexecuteGetClient({ requestPolicy: 'network-only' })
    },
    [acceptGlobalConsents, reexecuteGetClient]
  )

  const doCancel = useCallback(() => {
    navigate('/')
  }, [navigate])

  if (!isMissing) return null

  return (
    <Transition.Root show={isMissing} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        open={isMissing}
        onClose={doCancel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl p-5 bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <Dialog.Title className="text-lg mb-2.5 text-blue-700">
                  {i18n.t('components.missingGlobalConsentsModal.title')}
                </Dialog.Title>
                <p className="text-sm text-blue-700 mb-4">
                  {i18n.t('components.missingGlobalConsentsModal.description')}
                </p>
                <div className="mb-10">
                  {!client?.regulationsAcceptedAt && (
                    <div className="my-5 md:mx-5">
                      <Controller
                        control={control}
                        rules={{
                          required: i18n.t(
                            'components.missingGlobalConsentsModal.consentRequired'
                          ),
                        }}
                        name="regulationsAccepted"
                        render={({
                          field: { value, onChange, ref },
                          fieldState: { error: fieldError },
                        }) => (
                          <label className="flex" ref={ref}>
                            <Switch
                              checked={value}
                              onChange={onChange}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              width={40}
                              height={24}
                              onColor={Green600}
                              className="mr-6"
                            />
                            <span className="text-xs text-blue-700 pt-1">
                              {i18n.t(
                                'components.missingGlobalConsentsModal.regulations.part1'
                              )}{' '}
                              <a
                                href={
                                  RegulationLinks.regulation[i18n.locale] ||
                                  RegulationLinks.regulation.pl
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="font-bold"
                              >
                                {i18n.t(
                                  'components.missingGlobalConsentsModal.regulations.part2'
                                )}
                              </a>{' '}
                              {i18n.t(
                                'components.missingGlobalConsentsModal.regulations.part3'
                              )}{' '}
                              <a
                                href={
                                  RegulationLinks.privacyPolicy[i18n.locale] ||
                                  RegulationLinks.privacyPolicy.pl
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="font-bold"
                              >
                                {i18n.t(
                                  'components.missingGlobalConsentsModal.regulations.part4'
                                )}
                              </a>{' '}
                              {i18n.t(
                                'components.missingGlobalConsentsModal.regulations.part5'
                              )}
                              {fieldError && (
                                <p className="hl-input-error">
                                  {fieldError.message}
                                </p>
                              )}
                            </span>
                          </label>
                        )}
                      />
                    </div>
                  )}

                  {!client?.medicalDocumentationAcceptedAt && (
                    <div className="my-5 md:mx-5">
                      <Controller
                        control={control}
                        rules={{
                          required: i18n.t(
                            'components.missingGlobalConsentsModal.consentRequired'
                          ),
                        }}
                        name="medicalDocumentationAccepted"
                        render={({
                          field: { value, onChange, ref },
                          fieldState: { error: fieldError },
                        }) => (
                          <label className="flex" ref={ref}>
                            <Switch
                              checked={value}
                              onChange={onChange}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              width={40}
                              height={24}
                              onColor={Green600}
                              className="mr-6"
                            />
                            <span className="text-xs text-blue-700 pt-1">
                              {i18n.t(
                                'components.missingGlobalConsentsModal.medicalDocumentation'
                              )}
                              {fieldError && (
                                <p className="hl-input-error">
                                  {fieldError.message}
                                </p>
                              )}
                            </span>
                          </label>
                        )}
                      />
                    </div>
                  )}

                  {error && <p className="hl-input-error">{error}</p>}
                </div>
                <div className="flex justify-end gap-2.5">
                  <button
                    className="hl-button"
                    disabled={fetching}
                    onClick={handleSubmit(doSubmit)}
                  >
                    {i18n.t('components.missingGlobalConsentsModal.accept')}
                  </button>
                  <button
                    className="hl-button hl-gradient-red"
                    disabled={fetching}
                    onClick={doCancel}
                  >
                    {i18n.t('components.missingGlobalConsentsModal.cancel')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
